import { Component, ReactNode } from 'react'
import { withTranslation, WithTranslation } from 'react-i18next'
import { AppError } from '../utils/errorHandler'
import logger from '../services/logger'

interface Props extends WithTranslation {
  children: ReactNode
  fallback?: (error: AppError, reset: () => void) => ReactNode
}

interface State {
  hasError: boolean
  error: AppError | null
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error: {
        message: error.message || 'Something went wrong',
        details: error.stack,
        type: 'unknown',
        canRetry: true,
      },
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console in development
    if (import.meta.env.DEV) {
      logger.error('Error caught by boundary', error, { errorInfo })
    }

    // In production, you could send this to an error tracking service
    // Example: sendErrorToService(error, errorInfo)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError && this.state.error) {
      const { t } = this.props

      // If custom fallback is provided, use it
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleReset)
      }

      // Default error UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-sm p-8">
            <div className="flex flex-col items-center text-center space-y-4">
              <svg
                className="w-16 h-16 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>

              <div>
                <h2 className="text-xl font-semibold text-slate-900 mb-2">
                  {t('errors.unknown.title')}
                </h2>
                <p className="text-slate-600 mb-4">
                  {this.state.error.message}
                </p>
              </div>

              <div className="flex gap-3 w-full">
                <button
                  onClick={this.handleReset}
                  className="flex-1 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
                >
                  {t('errors.tryAgain')}
                </button>
                <button
                  onClick={() => window.location.href = '/'}
                  className="flex-1 px-4 py-2 bg-slate-200 text-slate-900 rounded-lg hover:bg-slate-300 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
                >
                  {t('errors.goHome')}
                </button>
              </div>

              {import.meta.env.DEV && this.state.error.details && (
                <details className="mt-4 w-full text-start">
                  <summary className="cursor-pointer text-sm text-slate-600 hover:text-slate-900">
                    Error details (dev only)
                  </summary>
                  <pre className="mt-2 p-3 bg-slate-100 rounded-sm text-xs overflow-auto max-h-40">
                    {this.state.error.details}
                  </pre>
                </details>
              )}
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

// Export with HOC wrapper for i18n
const ErrorBoundaryWithTranslation = withTranslation()(ErrorBoundary)
ErrorBoundaryWithTranslation.displayName = 'ErrorBoundaryWithTranslation'
export default ErrorBoundaryWithTranslation
